import { Button, Icon, IconButton } from "@material-ui/core";
import { CheckCircleRounded } from "@material-ui/icons";
import firebase from "firebase/compat/app";
import React, { Component } from "react";
import styled from "styled-components";

import {
    LibraryView,
    PresentationFilters
} from "common/constants";
import { ActionPermissionsObject, IWorkspaceResourcePresentation, IWorkspaceResourceWithDocument, PermissionActionType, WorkspaceResourceType } from "common/interfaces";
import { Button as BAIButton } from "js/Components/Button";
import InfoBanner from "js/Components/InfoBanner";
import { getStaticUrl } from "js/config";
import PresentationLibraryPaneController, {
    PresentationLibraryFilter,
    PresentationLibraryFilterType,
    PresentationLibraryPaneControllerState,
    PresentationLibraryPresentation
} from "js/controllers/PresentationLibraryPaneController";
import WorkspaceController, { getWorkspaceResourcesWithDocuments, WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { User } from "js/core/models/user";
import { OnMountedReporter } from "js/core/utilities/useMountedAt";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { AiIcon } from "js/editor/AiGeneration/AiIcon";
import { CreatePresentationDialogPaneType } from "js/editor/CreatePresentation/CreatePresentationDialogTypes";
import ShortcutPanel from "js/editor/PresentationEditor/Components/ShortCutPanel";
import { PresentationLibraryGrid } from "js/editor/PresentationLibrary/PresentationLibraryGrid";
import { PresentationLibraryList } from "js/editor/PresentationLibrary/PresentationLibraryList";
import { PresentationLibraryPaneControllerInitializer, PresentationLibraryPaneControllerInitializerProps } from "js/editor/PresentationLibrary/PresentationLibraryPaneControllerInitializer";
import { PresentationLibraryShareNoticeMessage } from "js/editor/PresentationLibrary/PresentationLibraryShareNoticeMessage";
import { PresentationLibrarySideBar } from "js/editor/PresentationLibrary/PresentationLibrarySideBar";
import PresentationLibraryViewSelect from "js/editor/PresentationLibrary/PresentationLibraryViewSelect";
import { PresentationListHeader } from "js/editor/PresentationLibrary/PresentationListHeader";
import { buildFolderPathParts } from "js/editor/PresentationLibrary/utils/folderPaths";
import { shouldShowPresentationLibraryViewSelect } from "js/editor/PresentationLibrary/utils/shouldShowPresentationLibraryViewSelect";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import {
    ShowConfirmationDialog,
    ShowErrorDialog,
    ShowMessageDialog
} from "js/react/components/Dialogs/BaseDialog";
import { FlexSpacer, Gap10, Gap20, Gap30, Gap5 } from "js/react/components/Gap";
import { FlexBox, ScrollBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, Notice, ResourceNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import {
    StyledWarningIcon,
    UIPane,
    UIPaneContents,
    UIPaneSideBar,
    UIPaneWithSideBar,
    WarningBox,
    WarningButton
} from "js/react/components/UiComponents";
import { browserHistory } from "js/react/history";
import { themeColors } from "js/react/sharedStyles";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";

const logger = getLogger(LogGroup.LIBRARY);

const CloseButtonContainer = styled.div < { isMobile?: boolean } > `
    text-align: right;
    margin-bottom: -15px;
    margin-top: 10px;
    padding: ${props => props.isMobile ? "0 20px" : "0"};
`;

const RemoteControlBanner = styled.div<{ blue?: boolean }>`
    padding: 10px;
    text-align: center;
    text-wrap: nowrap;
    background: ${props => props.blue ? themeColors.ui_blue : "orangered"};
    color: white;
    font-size: 18px;
    width: 100%;
    font-weight: 600;
`;

const SubFolderContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    max-height: 108px;
    overflow-y: auto;
    margin-bottom: 10px;
    gap: 5px;
    row-gap: 5px;
`;

const SubFolder = styled.div<{ isSelected: boolean }>`
    background: ${props => props.isSelected ? "white" : "#e1e1e1"};
    padding: 10px 15px 10px 10px;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: flex-start;

    span {
        font-weight: 400;
    }

    .MuiIcon-root {
        opacity: 0.5;
    }

    &:hover {
        background: ${themeColors.selection};
    }
`;

const CreatePresentationButton = styled(Button)`
    flex-shrink: 0;
    height: 50px;
    background: ${themeColors.ui_blue} !important;
    padding: 0px 20px 0px 10px !important;

    .MuiButton-label {
        color: white;
        font-family: "Source Sans Pro";
        font-size: 16px;
        font-weight: 600;
        margin-left: 10px;
    }

    .MuiIcon-root {
        background: white;
        color: black;
        border-radius: 50%;
        padding: 3px;
        font-size: 17px;
        margin-right: 6px;
    }
`;

const CreatePresentationButtonIntegrator = styled(CreatePresentationButton)`
    border: 1px solid ${themeColors.ui_blue} !important;
    padding: 0 !important;
    margin-right: 14px !important;

    .MuiButton-label {
        color: #444;
        background: transparent;
        padding: 10px 20px;
    }

    .dark-mode & {
        .MuiButton-label {
            color: #ccc;
        }
    }
`;

export const Breadcrumb = styled.div`
    font-size: 18px;
    text-transform: uppercase;
    margin-right: 10px;
    color: #444;
    flex-shrink: 0;

    .dark-mode & {
        color: #ccc;
    }
`;

const StyledRoundedIcon = styled(CheckCircleRounded)`
    &.MuiSvgIcon-colorPrimary {
        width: 16px;
        height: 16px;
        margin-right: 8px;
    }
`;

const StyledPresentationListHeader = styled(PresentationListHeader)`
    justify-content: space-between;
    width: 100%;
`;

const ButtonContent = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const StyledBAIButton = styled(BAIButton)`
    height: auto;
    width: 100%;
    padding: 4px 12px;

    span {
        padding: 6px 0;
        gap: 10px;
        height: auto;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const InfoBannerContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px 20px 0 20px;
`;

const HiddenScrollBox = styled(ScrollBox)`
    /* Hide scrollbar for WebKit browsers (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
        display: none;
    }

    /* Hide scrollbar for Firefox */
    scrollbar-width: none;

    /* Hide scrollbar for IE and Edge Legacy */
    -ms-overflow-style: none;
`;

const StyledRenderItemsContainer = styled.div`
    padding: 0 20px;
    box-sizing: border-box;
`;

type PresentationLibraryPaneProps = WorkspaceControllerState & PresentationLibraryPaneControllerState & {
    workspaceId: string;
    firebaseUser: firebase.User;
    readOnly?: boolean;
    pane?: PresentationLibraryFilterType;
    folderId?: string;
    subFolderId?: string;
    user: typeof User;
    onSelectedPresentation: (presentationId: string) => void;
    onCloseDialog: () => void;
    showCloseIcon: boolean;
    showSidebar: boolean;
    onHideSidebar: () => void;
    onSwitchType: () => void;
    sideBarWidth: number;
    onResizeSidebar: () => void;
    className: string;
    showLibraryViewSelect: boolean;
    isMyPresentationsOnly: boolean;
    onReady?: () => void;
    onDoubleClick?: () => void;
}

interface InnerPresentationLibraryPaneState {
    draggedItems: PresentationLibraryPresentation[];
    infoBannerClosed: boolean;
}

// Base class with shared logic
export class PresentationLibraryPaneBase extends Component<PresentationLibraryPaneProps, InnerPresentationLibraryPaneState> {
    sideBarRef: React.RefObject<any>;
    scrollRef: React.RefObject<any>;

    constructor(props) {
        super(props);

        this.sideBarRef = React.createRef();
        this.scrollRef = React.createRef();

        this.state = {
            draggedItems: [],
            infoBannerClosed: false
        };
    }

    handleInfoBannerClose = () => {
        localStorage.setItem("bai-mobile-info-banner-closed", "true");
        this.setState({ infoBannerClosed: true });
    }

    componentDidMount() {
        if (localStorage.getItem("bai-mobile-info-banner-closed") === "true") {
            this.setState({ infoBannerClosed: true });
        }
    }

    handleSetFilter = (filter: PresentationLibraryFilter) => {
        const { readOnly } = this.props;

        // if we are in read-only (We're adding a slide from My Slide sectio ) mode
        // we should just update the filter state because we
        // don't want to navigate away from the current page
        if (readOnly) {
            PresentationLibraryPaneController.setLibraryFilter(filter);
        } else {
            AppController.showLibrary({ filter });
        }
    }

    handleSelectedPresentations = async (selection: PresentationLibraryPresentation[]) => {
        const { onSelectedPresentation } = this.props;

        if (selection.length === 0) {
            return;
        }

        if (onSelectedPresentation) {
            onSelectedPresentation(selection[0].id);
            return;
        }

        if (app.isConstrained) {
            await this.handleDoubleClickedPresentation(selection[0]);
        }
    }

    handleDoubleClickedPresentation = async (presentation: PresentationLibraryPresentation) => {
        if (this.props.onDoubleClick) {
            return this.props.onDoubleClick();
        }

        if (presentation.trashedAt) {
            return ShowErrorDialog({
                title: t("Can't edit presentations in the trash"),
                message: t("Please restore this presentation back into your library before editing.")
            });
        }

        // zoom app cases (meeting and non-meeting views)
        if (window.isZoomApp) {
            if (window.roomID && window.roomPresentationStarted) {
                return await window.roomPresentationStarted(presentation.id)
                    .catch(err => ShowErrorDialog({
                        title: t("Error Starting Presentation"),
                        message: err.message
                    }));
            }

            // @ts-ignore
            if (!window.roomID && window.roomPresentationPreviewed) {
                // @ts-ignore
                return await window.roomPresentationPreviewed(presentation.id)
                    .catch(err => ShowErrorDialog({
                        title: t("Error Previewing Presentation"),
                        message: err.message
                    }));
            }
        }

        // show player if in a Meeting Room
        if (window.roomID) {
            PresentationLibraryPaneController.playPresentation(presentation.id);
            return;
        }

        // show player if user doesn't have write permissions or if the app is constrained mode
        if (!presentation.permissions.write || app.isConstrained) {
            PresentationLibraryPaneController.playPresentation(presentation.id, !app.isConstrained);
            return;
        }

        // show presentation editor
        AppController.showEditor({ presentationId: presentation.id });
    }

    handleStartDragItems = (selection: PresentationLibraryPresentation[]) => {
        this.setState({ draggedItems: selection });
    }

    handleDropDragItems = () => {
        this.setState({ draggedItems: [] });
    }

    handleSubFolderMouseUp = async (subFolderId: string) => {
        const { draggedItems } = this.state;

        const filter = PresentationLibraryPaneController.libraryFilter;

        if (draggedItems?.length) {
            const resources = draggedItems.map(presentation => ({
                resourceId: presentation.id,
                resourceType: WorkspaceResourceType.PRESENTATION,
                permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
            }));

            try {
                await withProgressDialog({
                    title: t("Adding {{count}} presentations to folder...", { count: resources.length }),
                    action: async () => {
                        await WorkspaceController.updateWorkspaceResourceFolderResources({
                            folderId: subFolderId,
                            update: {
                                add: resources,
                            }
                        });

                        this.handleSetFilter({
                            type: PresentationLibraryFilterType.TEAM_FOLDER,
                            folderId: filter.folderId,
                            subFolderId
                        });
                    }
                });
            } catch (err) {
                logger.error(err, "Error adding presentations to folder");

                ShowErrorDialog({
                    title: t("Error adding presentations to folder"),
                    message: err.message
                });
            }
        } else {
            this.handleSetFilter({
                type: PresentationLibraryFilterType.TEAM_FOLDER,
                folderId: filter.folderId,
                subFolderId
            });
        }
    }

    handleEmptyTrash = async () => {
        const { workspaceId } = this.props;

        const records: IWorkspaceResourceWithDocument<{ id: string, softDeletedAt: number }, IWorkspaceResourcePresentation>[] = [];

        let total = Number.MAX_SAFE_INTEGER;

        while (total > records.length) {
            const response = await getWorkspaceResourcesWithDocuments({
                offset: records.length,
                size: 100,
                workspaceId,
                resourceTypes: [WorkspaceResourceType.PRESENTATION],
                filters: [{ field: "document.softDeletedAt", value: null, operator: "ne" }],
                documentFields: ["id", "softDeletedAt"]
            });
            records.push(...response.items);
            total = response.total;
        }

        if (records.length === 0) {
            ShowMessageDialog({
                title: t("No presentations in trash"),
                message: t("There are no presentations in the trash.")
            });
            return;
        }

        ShowConfirmationDialog({
            title: t("Are you sure you want to permanently delete all of the presentations in the trash?"),
            message: t("You can't undo this action."),
            acceptCallback: () => {
                withProgressDialog({
                    title: t("Deleting presentations in trash..."),
                    action: () =>
                        PresentationLibraryPaneController.deletePresentations(records.map(i => i.document.id))
                            .catch(err => {
                                logger.error(err, "handleEmptyTrash() failed to delete presentations in trash");

                                ShowErrorDialog({
                                    title: t("Error deleting presentations in trash"),
                                    message: err.message
                                });
                            })
                });
            }
        });
    }

    handleCreateNewPresentation = () => {
        AppController.showCreatePresentationDialog({
            pane: CreatePresentationDialogPaneType.BLANK_PRESENTATION,
            libraryFilter: PresentationLibraryPaneController.libraryFilter
        });
    }

    getFilterData() {
        const { resourceFolders } = this.props;

        const filter = PresentationLibraryPaneController.libraryFilter;

        let subFolders = [];
        let breadcrumb;

        switch (filter.type) {
            case PresentationFilters.ALL_PRESENTATIONS:
                breadcrumb = t("All Presentations");
                break;
            case PresentationFilters.RECENT:
                breadcrumb = t("Recent Presentations");
                break;
            case PresentationFilters.OWNED_BY_ME:
                breadcrumb = t("Created by me");
                break;
            case PresentationFilters.SHARED_WITH_ME:
                breadcrumb = t("Shared With Me");
                break;
            case PresentationFilters.FOLDER:
            case PresentationFilters.TEAM_FOLDER: {
                const folderId = filter.subFolderId || filter.folderId;
                const folder = resourceFolders.find(f => f.id === folderId);
                if (folder) {
                    breadcrumb = buildFolderPathParts(folderId, resourceFolders).join(" > ");
                    subFolders = resourceFolders.filter(f => f.parentFolderId === folder.id);
                }
                break;
            }
            case PresentationFilters.TRASH:
                breadcrumb = t("Trash");
                break;
        }

        return { breadcrumb, subFolders };
    }

    renderItems() {
        const { readOnly, viewType, search, fetching } = this.props;

        if (!PresentationLibraryPaneController.initialized) {
            return <Spinner />;
        }

        const filter = PresentationLibraryPaneController.libraryFilter;
        const searchQuery = search.query;

        const aiBullets = [
            t("Create presentations in one click with AI"),
            t("Hundreds of Smart Slide templates"),
            t("Create your branded theme"),
            t("Be a presentation rockstar, make beautiful slides in minutes")
        ];

        const presentations = PresentationLibraryPaneController.legacyData;
        if (presentations.length === 0 && !fetching) {
            switch (filter.type) {
                case PresentationFilters.ALL_PRESENTATIONS:
                case PresentationFilters.OWNED_BY_ME:
                case PresentationFilters.RECENT:
                    //currently readOnly is set to true when viewing presentation library from CopySlidePane.
                    if (readOnly || searchQuery) {
                        return <NoMatchNotice />;
                    } else {
                        if (app.isConstrained) {
                            return (
                                <Notice
                                    title={t("You have no presentations in this workspace.")}
                                    message={t("Open Beautiful.ai in your desktop browser to create one.")}
                                />
                            );
                        } else {
                            if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCES, PermissionActionType.CREATE)) {
                                return (
                                    <ResourceNotice
                                        image={getStaticUrl("/images/notice-banner/library.svg")}
                                        title={t("There are no presentations in this workspace.")}
                                        message={t("You are a guest in this workspace. You can find presentations shared with you here.")}
                                    />
                                );
                            } else if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.GENERATIVE_AI, PermissionActionType.CREATE)) {
                                return (
                                    <ResourceNotice
                                        image={getStaticUrl("/images/notice-banner/library.svg")}
                                        title={t("You don't have any presentations yet.")}
                                        message={
                                            <>
                                                <FlexBox>{t("Create presentations in minutes with automated design.")}</FlexBox>
                                                <div style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "1fr 1fr",
                                                    gap: "15px",
                                                    marginTop: "10px",
                                                    backgroundColor: "#F7FAFC",
                                                    padding: "15px",
                                                    borderRadius: "2px",
                                                    justifyItems: "start"
                                                }}>
                                                    {aiBullets.map(text => (
                                                        <FlexBox key={text}>
                                                            <StyledRoundedIcon color="primary" />
                                                            <span>{text}</span>
                                                        </FlexBox>
                                                    ))}
                                                </div>
                                            </>
                                        }
                                    />
                                );
                            }
                            return (
                                <Notice
                                    title={t("Click Create Presentation to get started.")}
                                />
                            );
                        }
                    }
                case PresentationFilters.SHARED_WITH_ME:
                    return (
                        <Notice
                            title={t("No one has shared a presentation with you.")}
                            message={(
                                <PresentationLibraryShareNoticeMessage />
                            )}
                        />
                    );
                case PresentationFilters.TRASH:
                    return (
                        <Notice
                            title={t("Trash is empty")}
                        />
                    );
                case PresentationFilters.FOLDER:
                    return (
                        <Notice
                            title={t("This folder is empty.")}
                            message={!app.isConstrained && t("You can drag presentations into this folder to add them.")}
                        />
                    );
                case PresentationFilters.TEAM_FOLDER:
                    return (
                        <ResourceNotice
                            image={getStaticUrl(`/images/notice-banner/team-folders.svg`)}
                            title={t("Organize your work in shared Team Folders.")}
                            message={t("Collaborate seamlessly and keep your team's presentations organized and easy to find with Team Folders.")}
                        />
                    );
                default:
                    return (
                        <Notice
                            title={t("No presentations found")}
                        />
                    );
            }
        }

        if (viewType === "grid") {
            return (
                <PresentationLibraryGrid
                    readOnly={readOnly}
                    onNavigateToFolder={this.handleSetFilter}
                    onSelectedPresentations={this.handleSelectedPresentations}
                    onDoubleClick={this.handleDoubleClickedPresentation}
                    onStartDragItems={this.handleStartDragItems}
                    onDropDragItems={this.handleDropDragItems}
                />
            );
        }

        return (
            <PresentationLibraryList
                readOnly={readOnly}
                onNavigateToFolder={this.handleSetFilter}
                onSelectedPresentations={this.handleSelectedPresentations}
                onDoubleClick={this.handleDoubleClickedPresentation}
                onStartDragItems={this.handleStartDragItems}
                onDropDragItems={this.handleDropDragItems}
            />
        );
    }
}

// Desktop version
class InnerPresentationLibraryPaneDesktop extends PresentationLibraryPaneBase {
    render() {
        const {
            readOnly,
            onCloseDialog,
            showCloseIcon,
            showSidebar,
            onHideSidebar,
            onSwitchType,
            sideBarWidth,
            onResizeSidebar,
            className,
            showLibraryViewSelect = true,
            isMyPresentationsOnly = false,
            initialized,
            viewType,
            onReady = () => { }
        } = this.props;
        const { draggedItems } = this.state;

        if (!initialized) {
            return (<UIPane>
                <Spinner />
            </UIPane>);
        }

        const filter = PresentationLibraryPaneController.libraryFilter;

        const { breadcrumb, subFolders } = this.getFilterData();

        const canManageSharedSlides =
            !app.integrator &&
            !readOnly &&
            WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;

        const TrashHeader = filter.type === PresentationFilters.TRASH ? (
            <FlexBox middle right fillWidth style={{ paddingRight: 40 }}>
                <StyledWarningIcon>warning</StyledWarningIcon>
                <Gap10 />
                <WarningBox>
                    {t("Items in your trash will be permanently deleted after 30 days")}
                </WarningBox>
                <Gap20 />
                <WarningButton
                    // @ts-ignore
                    onClick={this.handleEmptyTrash}>
                    {t("Empty Trash")}
                </WarningButton>
            </FlexBox>
        ) : null;

        const canCreateResources = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCES].create;
        const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.USE);
        const shouldShowLibraryViewSelectControl = shouldShowPresentationLibraryViewSelect({
            isGuest: WorkspaceController.isGuest,
            canCreateResources,
            canUpgradeToUseSharedResources
        });
        return (
            // @ts-ignore
            <UIPaneWithSideBar className={className ?? ""}>
                <OnMountedReporter onMounted={onReady} />
                {!window.isZoomApp &&
                    <UIPaneSideBar
                        // @ts-ignore
                        show={showSidebar}
                        onHide={onHideSidebar}
                        style={{ width: sideBarWidth }}
                        onResize={onResizeSidebar}
                    >
                        {!app.isMobileOrTablet && showLibraryViewSelect && shouldShowLibraryViewSelectControl && <PresentationLibraryViewSelect libraryView={LibraryView.LIBRARY} onSwitchType={onSwitchType} isMyPresentationsOnly={isMyPresentationsOnly} />}
                        <PresentationLibrarySideBar
                            filter={filter}
                            onSetFilter={this.handleSetFilter}
                            readOnly={readOnly}
                            draggedItems={draggedItems}
                            canEditLibraryItems={canManageSharedSlides}
                            onHideSidebar={onHideSidebar}
                        />
                    </UIPaneSideBar>
                }

                {/* @ts-ignore */}
                <UIPaneContents style={{
                    paddingLeft: 30,
                    paddingRight: 30,
                    paddingBottom: 0
                }}>
                    {
                        showCloseIcon &&
                        <CloseButtonContainer>
                            <Icon onClick={onCloseDialog}>
                                close
                            </Icon>
                        </CloseButtonContainer>
                    }

                    {
                        app.integrator &&
                        <>
                            <Gap20 />
                            <CreatePresentationButtonIntegrator
                                id="new-presentation-button"
                                variant="text"
                                onClick={this.handleCreateNewPresentation}
                            >
                                <Icon>add</Icon>{t("Create New Presentation")}
                            </CreatePresentationButtonIntegrator>
                        </>
                    }

                    {window.roomID && (
                        window.isZoomApp
                            ? <RemoteControlBanner blue>{t("Select presentation to share in Zoom meeting.")}</RemoteControlBanner>
                            : <RemoteControlBanner>{t("Select a presentation to Remote Play")}</RemoteControlBanner>
                    )}
                    {!window.roomID && window.isZoomApp && (
                        <RemoteControlBanner blue>{t("Select a presentation to preview in player, please use the browser for full access.")}</RemoteControlBanner>
                    )}

                    <FlexBox left gap={20} middle style={{ height: 65 }}>
                        <Breadcrumb className="breadcrumb">{breadcrumb}</Breadcrumb>
                        {
                            filter.type != PresentationFilters.TRASH &&
                            <>
                                <FlexSpacer />
                                <PresentationListHeader
                                    viewType={viewType}
                                    searchPlaceholder={t("Search presentations")}
                                />
                            </>
                        }
                        {TrashHeader}
                    </FlexBox>
                    {subFolders.length > 0 &&
                        <SubFolderContainer>
                            {filter.subFolderId &&
                                <IconButton size="small"
                                    onMouseUp={() => this.handleSubFolderMouseUp(null)}><Icon>arrow_upward</Icon></IconButton>
                            }
                            {subFolders.map(subFolder => (
                                <SubFolder
                                    key={subFolder.id}
                                    onMouseUp={() => this.handleSubFolderMouseUp(subFolder.id)}
                                    isSelected={filter.subFolderId === subFolder.id}
                                >
                                    <Icon>folder_open</Icon>
                                    <Gap10 />
                                    {subFolder.name}
                                    <Gap5 />
                                    <span></span>
                                </SubFolder>)
                            )}
                        </SubFolderContainer>
                    }
                    <ScrollBox id="presentation_list" ref={this.scrollRef}
                        fillWidth
                        style={{ height: 0, flexGrow: 2, marginTop: 5, position: "relative" }}
                    >
                        {this.renderItems()}
                    </ScrollBox>
                </UIPaneContents>
                <ShortcutPanel />
            </UIPaneWithSideBar>
        );
    }
}

// Mobile version
class InnerPresentationLibraryPaneMobile extends PresentationLibraryPaneBase {
    render() {
        const {
            readOnly,
            onCloseDialog,
            showCloseIcon,
            showSidebar,
            onHideSidebar,
            sideBarWidth,
            onResizeSidebar,
            className,
            sort,
            viewType,
            onReady = () => { }
        } = this.props;
        const { draggedItems, infoBannerClosed } = this.state;

        const filter = PresentationLibraryPaneController.libraryFilter;

        const { breadcrumb } = this.getFilterData();

        const canManageSharedSlides =
            !app.integrator &&
            !readOnly &&
            WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;

        const TrashHeader = filter.type === PresentationFilters.TRASH ? (
            <FlexBox middle right fillWidth style={{ paddingRight: 40 }}>
                <StyledWarningIcon>warning</StyledWarningIcon>
                <Gap10 />
                <WarningBox>
                    {t("Items in your trash will be permanently deleted after 30 days")}
                </WarningBox>
                <Gap20 />
                <WarningButton
                    // @ts-ignore
                    onClick={this.handleEmptyTrash}>
                    {t("Empty Trash")}
                </WarningButton>
            </FlexBox>
        ) : null;

        return (
            // @ts-ignore
            <UIPaneWithSideBar className={className ?? ""}>
                <OnMountedReporter onMounted={onReady} />
                {!window.isZoomApp &&
                    <UIPaneSideBar
                        // @ts-ignore
                        show={showSidebar}
                        onHide={onHideSidebar}
                        style={{ width: sideBarWidth }}
                        onResize={onResizeSidebar}
                    >
                        <PresentationLibrarySideBar
                            filter={filter}
                            onSetFilter={this.handleSetFilter}
                            readOnly={readOnly}
                            draggedItems={draggedItems}
                            canEditLibraryItems={canManageSharedSlides}
                            onHideSidebar={onHideSidebar}
                        />
                    </UIPaneSideBar>
                }
                <UIPaneContents>
                    {
                        showCloseIcon &&
                        <CloseButtonContainer isMobile>
                            <Icon onClick={onCloseDialog}>
                                close
                            </Icon>
                        </CloseButtonContainer>
                    }

                    {window.roomID && (
                        window.isZoomApp
                            ? <RemoteControlBanner blue>{t("Select presentation to share in Zoom meeting.")}</RemoteControlBanner>
                            : <RemoteControlBanner>{t("Select a presentation to Remote Play")}</RemoteControlBanner>
                    )}
                    {!window.roomID && window.isZoomApp && (
                        <RemoteControlBanner blue>{t("Select a presentation to preview in player, please use the browser for full access.")}</RemoteControlBanner>
                    )}

                    {/* Scrollable content container */}
                    {/* @ts-ignore */}
                    <HiddenScrollBox id="presentation_list" ref={this.scrollRef}
                        fillWidth
                        style={{
                            height: 0,
                            flexGrow: 2,
                            position: "relative",
                            overflowY: "auto"
                        }}
                    >
                        {/* Content that scrolls away */}
                        <InfoBannerContainer>
                            {!infoBannerClosed && WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use && (<div>
                                <InfoBanner
                                    bodyText={t("Create with AI to get started. Switch to desktop to edit and make it your own.")}
                                    onClose={() => this.handleInfoBannerClose()}
                                />
                            </div>)}
                            {!window.isZoomApp && // @ts-ignore
                                <FlexBox middle>
                                    <Breadcrumb className="breadcrumb" style={{
                                        width: "100%",
                                        marginTop: 0,
                                        marginRight: 0
                                    }}>{breadcrumb}</Breadcrumb>
                                </FlexBox>
                            }

                            {WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use &&
                                <StyledBAIButton
                                    large
                                    ai
                                    onClick={() => browserHistory.push("/create-presentation/generate-with-ai")}>
                                    <ButtonContent>
                                        <AiIcon onClick={() => browserHistory.push("/create-presentation/generate-with-ai")} />
                                        {t("Create Presentation with AI...")}
                                    </ButtonContent>
                                </StyledBAIButton>

                            }
                        </InfoBannerContainer>

                        {/* Sticky search bar */}
                        <FlexBox fillWidth horizontalAlign="start" style={{
                            position: "sticky",
                            top: 0,
                            padding: "24px 20px",
                            // To make sure the search bar will always be on top of the presentation items
                            // even when the image is still loading
                            zIndex: 1001,
                            backgroundColor: "white",
                        }}>
                            {
                                filter.type != PresentationFilters.TRASH &&
                                <StyledPresentationListHeader
                                    viewType={viewType}
                                    sort={sort}
                                    searchPlaceholder={t("Search presentations")}
                                />
                            }
                            {TrashHeader}
                        </FlexBox>

                        {/* Presentation items */}
                        <StyledRenderItemsContainer>
                            {this.renderItems()}
                            <Gap30 />
                        </StyledRenderItemsContainer>
                    </HiddenScrollBox>
                </UIPaneContents>
                <ShortcutPanel />
            </UIPaneWithSideBar>
        );
    }
}

const InnerPresentationLibraryPane =
    AppController.withState(
        WorkspaceController.withState(
            PresentationLibraryPaneController.withState(
                withFirebaseUser(class InnerPresentationLibraryPane extends Component<PresentationLibraryPaneProps, InnerPresentationLibraryPaneState> {
                    render() {
                        if (app.isMobileOrTablet) {
                            return <InnerPresentationLibraryPaneMobile {...this.props} />;
                        }
                        return <InnerPresentationLibraryPaneDesktop {...this.props} />;
                    }
                }
                )
            )
        )
    );

export function PresentationLibraryPane(props: PresentationLibraryPaneProps & PresentationLibraryPaneControllerInitializerProps) {
    return (
        <PresentationLibraryPaneControllerInitializer pane={props.pane} folderId={props.folderId} subFolderId={props.subFolderId}>
            <InnerPresentationLibraryPane {...props} />
        </PresentationLibraryPaneControllerInitializer>
    );
}
